// Globals
import React, { ChangeEvent, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, feedSelector, getFeeds } from '../../feature';

// Components
import { StyledFilterRow, StyledSelect } from './FeedsFiltersContainerStyles';
import { Input, Select } from '@/ui';

// Modules
import { getCategoriesOptions, useCategoriesAutocomplete } from '@/modules/Categories';
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { FeedsFiltersFields, getActiveOptions } from '../../helpers';
import { GetFeedDTO } from '../../dtos';
import { useAutocompleteRegions } from '@/modules/Regions/hooks';

type IFeedsFiltersContainer = {
  children?: ReactNode;
  className?: string;
};

const FEED_TYPE_FILTER_OPTIONS = [
  { label: 'Google', value: 'google' },
  { label: 'Meta', value: 'meta' },
  { label: 'TikTok', value: 'tiktok' },
  { label: 'Google Local', value: 'google_local' },
];

const GENERATION_TYPE_FILTER_OPTIONS = [
  { label: 'Загальний', value: 'general' },
  { label: 'По виробникам', value: 'per_partner' },
];

const FeedsFiltersContainer = ({ children, className }: IFeedsFiltersContainer) => {
  const { t } = useTranslation(['seo']);
  const dispatch = useAppDispatch();
  const { query } = useAppState(feedSelector);
  const { canManage } = usePermissions();
  // The trailing actions column only exists when the admin can manage feeds.
  // Drop the spacer cell so the filter row keeps the same column count as the body.
  const canManageFeeds = canManage(Resource.FEED);
  const { list: categoriesList, onSearch: onCategoriesSearch } = useCategoriesAutocomplete({});
  const { onSearch: onSearchRegions, regionsOptions } = useAutocompleteRegions();

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetFeedDTO(searchParams);
        dispatch(getFeeds(dto));
        dispatch(changeQuery(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined, page: 1 };
    handleDebounceSearch(newQuery);
  };

  const handleBooleanFilter = (name: string) => (value: string) => {
    const newQuery = { ...query, [name]: value, page: 1 };
    handleDebounceSearch(newQuery);
  };

  const onSelectChange = (value: unknown, key: FeedsFiltersFields) => {
    const newQuery = { ...query, [key]: value || undefined, page: 1 };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input name={FeedsFiltersFields.NAME} onChange={handleSearch} size="large" allowClear />
        </th>
        <th>
          <Select
            options={FEED_TYPE_FILTER_OPTIONS}
            onChange={(v) => onSelectChange(v, FeedsFiltersFields.TYPE)}
            size="large"
            allowClear
          />
        </th>
        <th>
          <StyledSelect
            filterOption={false}
            options={regionsOptions ?? []}
            size="middle"
            onSearch={onSearchRegions}
            onChange={(v) => onSelectChange(v, FeedsFiltersFields.REGION_ID)}
            allowClear
            isMultiselect
            showSearch
            maxTagCount={1}
          />
        </th>
        <th>
          <StyledSelect
            allowClear
            showSearch
            options={getCategoriesOptions(categoriesList ?? [])}
            onSearch={onCategoriesSearch}
            onChange={(v) => onSelectChange(v, FeedsFiltersFields.CATEGORIES)}
            isMultiselect
            maxTagCount={1}
          />
        </th>

        <th />
        <th />
        <th />
        <th />

        <th>
          <Select
            options={getActiveOptions(t)}
            onChange={handleBooleanFilter(FeedsFiltersFields.ACTIVE)}
            size="large"
            allowClear
          />
        </th>
        {canManageFeeds && <th />}
        <th>
          <Select
            options={GENERATION_TYPE_FILTER_OPTIONS}
            onChange={(v) => onSelectChange(v, FeedsFiltersFields.GENERATION_TYPE)}
            size="large"
            allowClear
          />
        </th>
      </StyledFilterRow>
    </thead>
  );
};

export default FeedsFiltersContainer;
